import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { cobrettisAPI, expedientesAPI, contactosAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { 
  Plus, Search, Filter, AlertTriangle, TrendingUp, Euro, 
  FileText, Phone, Mail, Clock, CheckCircle2, XCircle, 
  ChevronLeft, ChevronRight, Eye, Edit, Trash2, RefreshCw,
  Bell, BellOff, DollarSign, Calendar, User, Building2
} from 'lucide-react';

// Constantes de colores por estado
const ESTADO_COLORS = {
  PENDIENTE: 'bg-gray-100 text-gray-800 border-gray-300',
  EN_RECLAMACION: 'bg-orange-100 text-orange-800 border-orange-300',
  EXTRAJUDICIAL: 'bg-red-100 text-red-800 border-red-300',
  DEMANDA_EJECUCION: 'bg-red-200 text-red-900 border-red-400',
  COBRADO: 'bg-green-100 text-green-800 border-green-300',
  FINALIZADO: 'bg-blue-100 text-blue-800 border-blue-300',
  CANCELADO: 'bg-gray-200 text-gray-600 border-gray-400',
};

const ESTADO_ICONS = {
  PENDIENTE: Clock,
  EN_RECLAMACION: Phone,
  EXTRAJUDICIAL: FileText,
  DEMANDA_EJECUCION: AlertTriangle,
  COBRADO: CheckCircle2,
  FINALIZADO: CheckCircle2,
  CANCELADO: XCircle,
};

// Formatear número de registro
const formatNumRegistro = (id) => {
  return `COB-${String(id).padStart(4, '0')}`;
};

// Componente de métrica del dashboard
const MetricCard = ({ title, value, subtitle, icon: Icon, color = 'blue', onClick }) => (
  <Card 
    className={`cursor-pointer hover:shadow-lg transition-shadow ${onClick ? 'hover:border-blue-400' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className={`text-2xl font-bold text-${color}-600`}>{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`p-3 bg-${color}-100 rounded-full`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Componente de badge de estado
const EstadoBadge = ({ estado }) => {
  const Icon = ESTADO_ICONS[estado] || Clock;
  const colorClass = ESTADO_COLORS[estado] || 'bg-gray-100 text-gray-800';
  
  const ESTADO_LABELS = {
    PENDIENTE: 'Pendiente',
    EN_RECLAMACION: 'En Reclamación',
    EXTRAJUDICIAL: 'Extrajudicial',
    DEMANDA_EJECUCION: 'Demanda Ejecución',
    COBRADO: 'Cobrado',
    FINALIZADO: 'Finalizado',
    CANCELADO: 'Cancelado',
  };
  
  return (
    <Badge className={`${colorClass} border flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {ESTADO_LABELS[estado] || estado}
    </Badge>
  );
};

// Formateo de moneda
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { 
    style: 'currency', 
    currency: 'EUR' 
  }).format(amount || 0);
};

// Formateo de fecha
const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

// Días desde última fecha
const diasDesde = (dateString) => {
  if (!dateString) return null;
  const fecha = new Date(dateString);
  const hoy = new Date();
  const diff = Math.floor((hoy - fecha) / (1000 * 60 * 60 * 24));
  return diff;
};

export default function Cobrettis() {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Estado principal
  const [cobrettis, setCobrettis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [alertas, setAlertas] = useState([]);
  
  // Filtros y paginación
  const [filters, setFilters] = useState({
    estado: '',
    tipo_deuda: '',
    search: '',
    page: 1,
    page_size: 50
  });
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Modales
  const [showNuevoModal, setShowNuevoModal] = useState(false);
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [selectedCobretti, setSelectedCobretti] = useState(null);
  
  // Formulario nuevo cobretti
  const [nuevoForm, setNuevoForm] = useState({
    expediente_id: '',
    expediente_numero: '',
    contacto_deudor_id: '',
    contacto_deudor_nombre: '',
    tipo_deuda: 'COSTAS',
    concepto: '',
    importe_principal: '',
    importe_intereses: '0',
    notas: ''
  });
  
  // Formulario editar cobretti
  const [editarForm, setEditarForm] = useState({
    id: null,
    tipo_deuda: 'COSTAS',
    concepto: '',
    importe_principal: '',
    importe_intereses: '0',
    notas: ''
  });
  
  // Búsqueda de expediente
  const [expedienteSearch, setExpedienteSearch] = useState('');
  const [expedienteResults, setExpedienteResults] = useState([]);
  const [searchingExpediente, setSearchingExpediente] = useState(false);
  
  // Búsqueda de contacto
  const [contactoSearch, setContactoSearch] = useState('');
  const [contactoResults, setContactoResults] = useState([]);
  const [searchingContacto, setSearchingContacto] = useState(false);
  
  // Configuración
  const [estados, setEstados] = useState([]);
  const [tiposDeuda, setTiposDeuda] = useState([]);

  // Cargar datos iniciales
  useEffect(() => {
    loadConfig();
    loadDashboard();
    loadAlertas();
  }, []);

  // Cargar lista cuando cambian filtros
  useEffect(() => {
    loadCobrettis();
  }, [filters]);

  const loadConfig = async () => {
    try {
      const [estadosRes, tiposRes] = await Promise.all([
        cobrettisAPI.getEstados(),
        cobrettisAPI.getTiposDeuda()
      ]);
      setEstados(estadosRes.data.estados || []);
      setTiposDeuda(tiposRes.data.tipos || []);
    } catch (error) {
      console.error('Error cargando configuración:', error);
    }
  };

  const loadDashboard = async () => {
    try {
      const response = await cobrettisAPI.getDashboard();
      setDashboard(response.data);
    } catch (error) {
      console.error('Error cargando dashboard:', error);
    }
  };

  const loadAlertas = async () => {
    try {
      const response = await cobrettisAPI.getAlertas({ leida: false, limit: 10 });
      setAlertas(response.data || []);
    } catch (error) {
      console.error('Error cargando alertas:', error);
    }
  };

  const loadCobrettis = async () => {
    setLoading(true);
    try {
      const params = { ...filters };
      // Limpiar parámetros vacíos
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });
      
      const response = await cobrettisAPI.getAll(params);
      setCobrettis(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      console.error('Error cargando cobrettis:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar los registros',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  // Búsqueda de expediente
  const searchExpedientes = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setExpedienteResults([]);
      return;
    }
    
    setSearchingExpediente(true);
    try {
      const response = await expedientesAPI.getAll({ search: query, limit: 10 });
      setExpedienteResults(response.data.data || response.data.items || response.data || []);
    } catch (error) {
      console.error('Error buscando expedientes:', error);
    } finally {
      setSearchingExpediente(false);
    }
  }, []);

  // Búsqueda de contacto
  const searchContactos = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setContactoResults([]);
      return;
    }
    
    setSearchingContacto(true);
    try {
      const response = await contactosAPI.getAll({ search: query, limit: 10 });
      setContactoResults(response.data.data || response.data.items || response.data || []);
    } catch (error) {
      console.error('Error buscando contactos:', error);
    } finally {
      setSearchingContacto(false);
    }
  }, []);

  // Seleccionar expediente
  const selectExpediente = (expediente) => {
    // Auto-completar deudor con el cliente del expediente si existe
    const updates = {
      expediente_id: expediente.id,
      expediente_numero: expediente.numero_expediente
    };
    
    // Si el expediente tiene cliente, usarlo como deudor por defecto
    if (expediente.cliente_id && expediente.cliente_nombre) {
      updates.contacto_deudor_id = expediente.cliente_id;
      updates.contacto_deudor_nombre = expediente.cliente_nombre;
      setContactoSearch(expediente.cliente_nombre);
    }
    
    setNuevoForm(prev => ({ ...prev, ...updates }));
    setExpedienteSearch(expediente.numero_expediente);
    setExpedienteResults([]);
  };

  // Seleccionar contacto
  const selectContacto = (contacto) => {
    // Usar nombre_completo si existe, sino combinar nombre + apellidos
    const nombreCompleto = contacto.nombre_completo || 
      `${contacto.nombre || ''} ${contacto.primer_apellido || ''} ${contacto.segundo_apellido || ''}`.trim() ||
      contacto.nombre;
    
    setNuevoForm(prev => ({
      ...prev,
      contacto_deudor_id: contacto.id,
      contacto_deudor_nombre: nombreCompleto
    }));
    setContactoSearch(nombreCompleto);
    setContactoResults([]);
  };

  // Crear nuevo cobretti
  const handleCrearCobretti = async () => {
    // Validaciones
    if (!nuevoForm.expediente_id) {
      toast({ title: 'Error', description: 'Seleccione un expediente', variant: 'destructive' });
      return;
    }
    if (!nuevoForm.contacto_deudor_id) {
      toast({ title: 'Error', description: 'Seleccione un deudor', variant: 'destructive' });
      return;
    }
    if (!nuevoForm.concepto) {
      toast({ title: 'Error', description: 'Ingrese el concepto de la deuda', variant: 'destructive' });
      return;
    }
    if (!nuevoForm.importe_principal || parseFloat(nuevoForm.importe_principal) <= 0) {
      toast({ title: 'Error', description: 'Ingrese un importe válido', variant: 'destructive' });
      return;
    }

    try {
      const data = {
        expediente_id: parseInt(nuevoForm.expediente_id),
        contacto_deudor_id: parseInt(nuevoForm.contacto_deudor_id),
        tipo_deuda: nuevoForm.tipo_deuda,
        concepto: nuevoForm.concepto,
        importe_principal: parseFloat(nuevoForm.importe_principal),
        importe_intereses: parseFloat(nuevoForm.importe_intereses || 0),
        notas: nuevoForm.notas || null
      };

      const response = await cobrettisAPI.create(data);
      
      toast({
        title: 'Éxito',
        description: 'Registro de cobro creado correctamente'
      });
      
      setShowNuevoModal(false);
      resetNuevoForm();
      loadCobrettis();
      loadDashboard();
      
      // Abrir detalle del nuevo registro
      setSelectedCobretti(response.data);
      setShowDetalleModal(true);
    } catch (error) {
      console.error('Error creando cobretti:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo crear el registro',
        variant: 'destructive'
      });
    }
  };

  const resetNuevoForm = () => {
    setNuevoForm({
      expediente_id: '',
      expediente_numero: '',
      contacto_deudor_id: '',
      contacto_deudor_nombre: '',
      tipo_deuda: 'COSTAS',
      concepto: '',
      importe_principal: '',
      importe_intereses: '0',
      notas: ''
    });
    setExpedienteSearch('');
    setContactoSearch('');
    setExpedienteResults([]);
    setContactoResults([]);
  };

  // Abrir detalle
  const handleVerDetalle = async (cobretti) => {
    try {
      const response = await cobrettisAPI.getById(cobretti.id);
      setSelectedCobretti(response.data);
      setShowDetalleModal(true);
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el detalle',
        variant: 'destructive'
      });
    }
  };

  // Marcar alerta como leída
  const handleMarcarAlertaLeida = async (alertaId) => {
    try {
      await cobrettisAPI.marcarAlertaLeida(alertaId);
      setAlertas(prev => prev.filter(a => a.id !== alertaId));
    } catch (error) {
      console.error('Error marcando alerta:', error);
    }
  };

  // Filtrar por estado desde dashboard
  const handleFilterByEstado = (estado) => {
    setFilters(prev => ({ ...prev, estado, page: 1 }));
  };

  // Abrir modal de edición
  const handleEditarRegistro = (item) => {
    setEditarForm({
      id: item.id,
      tipo_deuda: item.tipo_deuda || 'COSTAS',
      concepto: item.concepto || '',
      importe_principal: item.importe_principal?.toString() || '',
      importe_intereses: item.importe_intereses?.toString() || '0',
      notas: item.notas || ''
    });
    setShowEditarModal(true);
  };

  // Eliminar registro
  const handleEliminarRegistro = async (item) => {
    if (!window.confirm(`¿Estás seguro de eliminar el registro COBRETTI del expediente ${item.expediente_numero}?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }
    
    try {
      await cobrettisAPI.delete(item.id);
      toast({ title: 'Éxito', description: 'Registro eliminado correctamente' });
      loadCobrettis();
      loadDashboard();
    } catch (error) {
      console.error('Error eliminando:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo eliminar el registro',
        variant: 'destructive'
      });
    }
  };

  // Guardar edición
  const handleGuardarEdicion = async () => {
    if (!editarForm.concepto) {
      toast({ title: 'Error', description: 'El concepto es obligatorio', variant: 'destructive' });
      return;
    }
    if (!editarForm.importe_principal || parseFloat(editarForm.importe_principal) <= 0) {
      toast({ title: 'Error', description: 'El importe principal debe ser mayor a 0', variant: 'destructive' });
      return;
    }

    try {
      await cobrettisAPI.update(editarForm.id, {
        tipo_deuda: editarForm.tipo_deuda,
        concepto: editarForm.concepto,
        importe_principal: parseFloat(editarForm.importe_principal),
        importe_intereses: parseFloat(editarForm.importe_intereses || 0),
        notas: editarForm.notas || null
      });

      toast({ title: 'Éxito', description: 'Registro actualizado correctamente' });
      setShowEditarModal(false);
      loadCobrettis();
      loadDashboard();
    } catch (error) {
      console.error('Error actualizando:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo actualizar el registro',
        variant: 'destructive'
      });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="cobrettis-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">COBRETTIS</h1>
          <p className="text-gray-500">Gestión de cobro de costas e impagos</p>
        </div>
        <Button 
          onClick={() => setShowNuevoModal(true)}
          className="bg-blue-600 hover:bg-blue-700"
          data-testid="btn-nuevo-cobretti"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo COBRETTI
        </Button>
      </div>

      {/* Dashboard Métricas */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Deuda Pendiente Total"
            value={formatCurrency(dashboard.total_deuda_pendiente)}
            icon={Euro}
            color="red"
          />
          <MetricCard
            title="Cobrado este Mes"
            value={formatCurrency(dashboard.total_cobrado_mes)}
            subtitle={`Mes anterior: ${formatCurrency(dashboard.total_cobrado_mes_anterior)}`}
            icon={TrendingUp}
            color="green"
          />
          <MetricCard
            title="Tasa de Recuperación"
            value={`${dashboard.tasa_recuperacion}%`}
            icon={CheckCircle2}
            color="blue"
          />
          <MetricCard
            title="Total Registros"
            value={dashboard.total_registros}
            subtitle={`${dashboard.alertas_pendientes} alertas pendientes`}
            icon={FileText}
            color="gray"
          />
        </div>
      )}

      {/* Filtros por estado */}
      {dashboard?.por_estado && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filters.estado === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => handleFilterByEstado('')}
          >
            Todos ({total})
          </Button>
          {estados.map(estado => (
            <Button
              key={estado.id}
              variant={filters.estado === estado.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleFilterByEstado(estado.id)}
              style={{ 
                backgroundColor: filters.estado === estado.id ? estado.color : 'transparent',
                borderColor: estado.color,
                color: filters.estado === estado.id ? 'white' : estado.color
              }}
            >
              {estado.nombre} ({dashboard.por_estado[estado.id] || 0})
            </Button>
          ))}
        </div>
      )}

      {/* Panel de Alertas */}
      {alertas.length > 0 && (
        <Card className="border-orange-200 bg-orange-50">
          <CardHeader className="py-3">
            <CardTitle className="text-sm flex items-center gap-2 text-orange-800">
              <Bell className="h-4 w-4" />
              Alertas Pendientes ({alertas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="py-2">
            <div className="space-y-2">
              {alertas.slice(0, 5).map(alerta => (
                <div key={alerta.id} className="flex items-center justify-between bg-white p-2 rounded border">
                  <div className="flex-1">
                    <p className="text-sm font-medium">{alerta.mensaje}</p>
                    <p className="text-xs text-gray-500">
                      {alerta.expediente_numero} - {alerta.deudor_nombre}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleMarcarAlertaLeida(alerta.id)}
                  >
                    <BellOff className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filtros y búsqueda */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Buscar por expediente, deudor o concepto..."
                  value={filters.search}
                  onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value, page: 1 }))}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <Select
              value={filters.tipo_deuda || "ALL"}
              onValueChange={(value) => setFilters(prev => ({ ...prev, tipo_deuda: value === "ALL" ? "" : value, page: 1 }))}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Tipo de deuda" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos los tipos</SelectItem>
                {tiposDeuda.map(tipo => (
                  <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setFilters({ estado: '', tipo_deuda: '', search: '', page: 1, page_size: 50 });
              }}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Limpiar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabla de registros */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="cobrettis-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nº Registro</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expediente</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deudor</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tipo</th>
                  <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Concepto</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Importe</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Cobrado</th>
                  <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pendiente</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Estado</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Ronda</th>
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                      Cargando...
                    </td>
                  </tr>
                ) : cobrettis.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="px-4 py-8 text-center text-gray-500">
                      No se encontraron registros
                    </td>
                  </tr>
                ) : (
                  cobrettis.map((item) => {
                    const diasSinContacto = diasDesde(item.fecha_ultimo_contacto);
                    const alertaInactividad = diasSinContacto !== null && diasSinContacto > 15;
                    const isTerminal = ['COBRADO', 'FINALIZADO', 'CANCELADO'].includes(item.estado);
                    
                    return (
                      <tr key={item.id} className="hover:bg-gray-50" data-testid={`row-${item.id}`}>
                        <td className="px-3 py-3">
                          <span className="font-mono text-sm font-bold text-blue-600">
                            {formatNumRegistro(item.id)}
                          </span>
                        </td>
                        <td className="px-3 py-3">
                          <Link 
                            to={`/expedientes/${item.expediente_id}`}
                            className="text-blue-600 hover:underline font-medium text-sm"
                          >
                            {item.expediente_numero}
                          </Link>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-sm font-medium text-gray-900">{item.contacto_deudor_nombre}</div>
                          {item.contacto_deudor_email && (
                            <div className="text-xs text-gray-500">{item.contacto_deudor_email}</div>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <Badge variant="outline" className="text-xs">{item.tipo_deuda}</Badge>
                        </td>
                        <td className="px-3 py-3">
                          <div className="text-sm text-gray-900 max-w-[180px] truncate" title={item.concepto}>
                            {item.concepto}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-sm">
                          {formatCurrency(item.importe_total)}
                        </td>
                        <td className="px-3 py-3 text-right text-green-600 text-sm">
                          {formatCurrency(item.importe_cobrado)}
                        </td>
                        <td className="px-3 py-3 text-right font-bold text-red-600 text-sm">
                          {formatCurrency(item.importe_pendiente)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <EstadoBadge estado={item.estado} />
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Badge variant="outline">{item.ronda_actual || 1}</Badge>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleVerDetalle(item)}
                              title="Ver detalle"
                              data-testid={`btn-ver-${item.id}`}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                            {!isTerminal && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditarRegistro(item)}
                                title="Editar"
                                data-testid={`btn-editar-${item.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEliminarRegistro(item)}
                              title="Eliminar"
                              className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              data-testid={`btn-eliminar-${item.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-sm text-gray-500">
                Mostrando {((filters.page - 1) * filters.page_size) + 1} - {Math.min(filters.page * filters.page_size, total)} de {total}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="px-3 py-1 text-sm">
                  Página {filters.page} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal: Nuevo COBRETTI */}
      <Dialog open={showNuevoModal} onOpenChange={(open) => { if (!open) resetNuevoForm(); setShowNuevoModal(open); }}>
        <DialogContent className="max-w-2xl" data-testid="modal-nuevo-cobretti">
          <DialogHeader>
            <DialogTitle>Nuevo Registro de Cobro</DialogTitle>
            <DialogDescription>
              Cree un nuevo registro para gestionar el cobro de una deuda
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Búsqueda de expediente */}
            <div className="space-y-2">
              <Label>Expediente *</Label>
              <div className="relative">
                <Input
                  placeholder="Buscar expediente por número..."
                  value={expedienteSearch}
                  onChange={(e) => {
                    setExpedienteSearch(e.target.value);
                    searchExpedientes(e.target.value);
                  }}
                  data-testid="input-expediente"
                />
                {searchingExpediente && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
                {expedienteResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {expedienteResults.map(exp => (
                      <div
                        key={exp.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => selectExpediente(exp)}
                      >
                        <div className="font-medium">{exp.numero_expediente}</div>
                        <div className="text-xs text-gray-500">{exp.titulo}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {nuevoForm.expediente_id && (
                <div className="text-sm text-green-600">
                  ✓ Expediente seleccionado: {nuevoForm.expediente_numero}
                </div>
              )}
            </div>

            {/* Búsqueda de deudor */}
            <div className="space-y-2">
              <Label>Deudor *</Label>
              <div className="relative">
                <Input
                  placeholder="Buscar contacto deudor..."
                  value={contactoSearch}
                  onChange={(e) => {
                    setContactoSearch(e.target.value);
                    searchContactos(e.target.value);
                  }}
                  data-testid="input-deudor"
                />
                {searchingContacto && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <RefreshCw className="h-4 w-4 animate-spin text-gray-400" />
                  </div>
                )}
                {contactoResults.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-md shadow-lg max-h-48 overflow-auto">
                    {contactoResults.map(cont => (
                      <div
                        key={cont.id}
                        className="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                        onClick={() => selectContacto(cont)}
                      >
                        <div className="font-medium">{cont.nombre_completo || cont.nombre}</div>
                        <div className="text-xs text-gray-500">{cont.nif_cif} {cont.email || cont.telefono || ''}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {nuevoForm.contacto_deudor_id && (
                <div className="text-sm text-green-600">
                  ✓ Deudor seleccionado: {nuevoForm.contacto_deudor_nombre}
                </div>
              )}
            </div>

            {/* Tipo de deuda */}
            <div className="space-y-2">
              <Label>Tipo de Deuda *</Label>
              <Select
                value={nuevoForm.tipo_deuda}
                onValueChange={(value) => setNuevoForm(prev => ({ ...prev, tipo_deuda: value }))}
              >
                <SelectTrigger data-testid="select-tipo-deuda">
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDeuda.map(tipo => (
                    <SelectItem key={tipo.id} value={tipo.id}>{tipo.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Concepto */}
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Textarea
                placeholder="Describa el concepto de la deuda..."
                value={nuevoForm.concepto}
                onChange={(e) => setNuevoForm(prev => ({ ...prev, concepto: e.target.value }))}
                rows={2}
                data-testid="input-concepto"
              />
            </div>

            {/* Importes */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe Principal (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={nuevoForm.importe_principal}
                  onChange={(e) => setNuevoForm(prev => ({ ...prev, importe_principal: e.target.value }))}
                  data-testid="input-importe-principal"
                />
              </div>
              <div className="space-y-2">
                <Label>Importe Intereses (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={nuevoForm.importe_intereses}
                  onChange={(e) => setNuevoForm(prev => ({ ...prev, importe_intereses: e.target.value }))}
                  data-testid="input-importe-intereses"
                />
              </div>
            </div>

            {/* Total calculado */}
            {(nuevoForm.importe_principal || nuevoForm.importe_intereses) && (
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Importe Total:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(
                      (parseFloat(nuevoForm.importe_principal) || 0) + 
                      (parseFloat(nuevoForm.importe_intereses) || 0)
                    )}
                  </span>
                </div>
              </div>
            )}

            {/* Notas */}
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                placeholder="Notas adicionales (opcional)..."
                value={nuevoForm.notas}
                onChange={(e) => setNuevoForm(prev => ({ ...prev, notas: e.target.value }))}
                rows={2}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { resetNuevoForm(); setShowNuevoModal(false); }}>
              Cancelar
            </Button>
            <Button onClick={handleCrearCobretti} data-testid="btn-guardar-cobretti">
              Crear Registro
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Detalle COBRETTI (versión simplificada) */}
      <Dialog open={showDetalleModal} onOpenChange={setShowDetalleModal}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="modal-detalle-cobretti">
          {selectedCobretti && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-xl">
                      {selectedCobretti.expediente_numero} - {selectedCobretti.contacto_deudor_nombre}
                    </DialogTitle>
                    <DialogDescription>
                      {selectedCobretti.concepto}
                    </DialogDescription>
                  </div>
                  <EstadoBadge estado={selectedCobretti.estado} />
                </div>
              </DialogHeader>

              <div className="py-4 space-y-6">
                {/* Resumen financiero */}
                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-gray-500">Importe Total</p>
                      <p className="text-2xl font-bold">{formatCurrency(selectedCobretti.importe_total)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-green-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-green-600">Cobrado</p>
                      <p className="text-2xl font-bold text-green-600">{formatCurrency(selectedCobretti.importe_cobrado)}</p>
                    </CardContent>
                  </Card>
                  <Card className="bg-red-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-red-600">Pendiente</p>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(selectedCobretti.importe_pendiente)}</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Información general */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Información General</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Tipo de deuda:</span>
                        <span className="ml-2 font-medium">{selectedCobretti.tipo_deuda}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Ronda actual:</span>
                        <span className="ml-2 font-medium">{selectedCobretti.ronda_actual}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Creado:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedCobretti.fecha_creacion)}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Último contacto:</span>
                        <span className="ml-2 font-medium">{formatDate(selectedCobretti.fecha_ultimo_contacto) || '-'}</span>
                      </div>
                    </div>
                    {selectedCobretti.notas && (
                      <div className="mt-4 p-3 bg-gray-50 rounded">
                        <p className="text-sm text-gray-500">Notas:</p>
                        <p className="text-sm">{selectedCobretti.notas}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Acciones rápidas */}
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" onClick={() => setShowDetalleModal(false)}>
                    Cerrar
                  </Button>
                  <Button 
                    onClick={() => {
                      setShowDetalleModal(false);
                      navigate(`/cobrettis/${selectedCobretti.id}`);
                    }}
                  >
                    Ver Detalle Completo
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal: Editar COBRETTI */}
      <Dialog open={showEditarModal} onOpenChange={setShowEditarModal}>
        <DialogContent className="max-w-xl" data-testid="modal-editar-cobretti">
          <DialogHeader>
            <DialogTitle>Editar Registro de Cobro</DialogTitle>
            <DialogDescription>
              {editarForm.id && `Registro ${formatNumRegistro(editarForm.id)}`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Deuda</Label>
              <Select
                value={editarForm.tipo_deuda}
                onValueChange={(v) => setEditarForm(p => ({ ...p, tipo_deuda: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDeuda.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Textarea
                value={editarForm.concepto}
                onChange={(e) => setEditarForm(p => ({ ...p, concepto: e.target.value }))}
                placeholder="Descripción del concepto de la deuda..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe Principal (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editarForm.importe_principal}
                  onChange={(e) => setEditarForm(p => ({ ...p, importe_principal: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Importe Intereses (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editarForm.importe_intereses}
                  onChange={(e) => setEditarForm(p => ({ ...p, importe_intereses: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            {(editarForm.importe_principal || editarForm.importe_intereses) && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-blue-800">Nuevo Importe Total:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(
                      (parseFloat(editarForm.importe_principal) || 0) + 
                      (parseFloat(editarForm.importe_intereses) || 0)
                    )}
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                value={editarForm.notas}
                onChange={(e) => setEditarForm(p => ({ ...p, notas: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarModal(false)}>Cancelar</Button>
            <Button onClick={handleGuardarEdicion}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
